import { DataProxy } from 'apollo-cache'
import produce from 'immer'

import {
  GetProjectSharesDocument,
  GetProjectSharesQuery,
  GetProjectSharesQueryVariables,
  ShareListItemFragment,
  ShareListItemFragmentDoc,
  GetProjectsDocument,
  GetProjectsQueryVariables,
} from 'gqlTypes'
import { dataIdFromObject } from 'graphql/apolloClient'

type ProjectRecipeFunction = (cachedObject: GetProjectSharesQuery) => void

interface FilterArguments {
  projectId: string
  search?: string | null
}

export const errorPreventiveCacheRead = <Query, Variables>(
  cache: DataProxy,
  readOptions: DataProxy.Query<Variables>
) => {
  /*
  Apollo seems to throw errors when he can't find an object in cache.
  The Apollo repo already has a open issue for it

  https://github.com/apollographql/apollo-feature-requests/issues/1

  Remove this code after this issue is solved.
  */
  try {
    return cache.readQuery<Query, Variables>(readOptions)
  } catch (e) {
    return null
  }
}

const createProjectQuery = ({ projectId, search = null }: FilterArguments) => {
  return {
    query: GetProjectSharesDocument,
    variables: {
      shortId: projectId,
      after: null,
      search: {
        name: search,
        isCurrentVersionDownloadable: null,
      },
    },
  }
}

export const readProjectList = (
  cache: DataProxy,
  filterArguments: FilterArguments
) => {
  const cacheArguments = createProjectQuery(filterArguments)

  return errorPreventiveCacheRead<
    GetProjectSharesQuery,
    GetProjectSharesQueryVariables
  >(cache, cacheArguments)
}

export const updateProjectList = (
  cache: DataProxy,
  filterArguments: FilterArguments,
  recipe: ProjectRecipeFunction
) => {
  const cachedProject = readProjectList(cache, filterArguments)

  if (!cachedProject) {
    return
  }

  const project = produce(cachedProject, recipe)

  cache.writeQuery<GetProjectSharesQuery, GetProjectSharesQueryVariables>({
    ...createProjectQuery(filterArguments),
    data: project,
  })

  return project
}

const createShareFragmentQuery = (shareIdentifier: string) => {
  return {
    fragment: ShareListItemFragmentDoc,
    fragmentName: 'ShareListItem',
    id: dataIdFromObject({
      __typename: 'Share',
      identifier: shareIdentifier,
    }) as string,
  }
}

export const readShareWithProjectFragment = (
  cache: DataProxy,
  shareIdentifier: string
) => {
  const cacheArguments = createShareFragmentQuery(shareIdentifier)

  return cache.readFragment<ShareListItemFragment>(cacheArguments)
}

type ShareRecipeFunction = (cachedObject: ShareListItemFragment) => void

export const updateShareWithProjectFragment = (
  cache: DataProxy,
  shareIdentifier: string,
  recipe: ShareRecipeFunction
) => {
  const cachedShare = readShareWithProjectFragment(cache, shareIdentifier)

  if (!cachedShare) {
    return
  }

  const share = produce(cachedShare, recipe)

  cache.writeFragment({
    ...createShareFragmentQuery(shareIdentifier),
    data: share,
  })

  return share
}

export const deleteWorkspaceProject = (
  cache: DataProxy,
  workspaceId: string,
  projectId: string
) => {
  const queryParams: DataProxy.Query<GetProjectsQueryVariables> = {
    query: GetProjectsDocument,
    variables: { workspaceId },
  }

  interface WorkspaceProjectsProps {
    workspace: {
      projects: {
        entries: { shortId: string }[]
        meta: {
          totalCount: number
        }
      }
    }
  }

  const workspaceProjects = cache.readQuery<WorkspaceProjectsProps>(queryParams)

  const updatedWorkspaceProjects = Object.assign({}, workspaceProjects)
  const entries = updatedWorkspaceProjects?.workspace?.projects?.entries || []
  const updatedEntries = entries.filter(entry => entry?.shortId !== projectId)

  updatedWorkspaceProjects.workspace.projects.entries = updatedEntries

  updatedWorkspaceProjects.workspace.projects.meta.totalCount =
    updatedEntries.length

  cache.writeQuery({
    ...queryParams,
    data: updatedWorkspaceProjects,
  })
}
